import { HttpErrorResponse } from '@angular/common/http';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';

import { ApiErrorResponse } from '../models';

/**
 * Handles HTTP error responses with validation error support and displays snackbar notifications
 * @param error The HttpErrorResponse from the failed request
 * @param fallbackMessageKey Translation key for generic error message
 * @param localeService The LocaleService instance for translations
 * @param validationKeyPrefix Prefix for validation message translation keys (e.g., 'TGA.Validation.Backend')
 * @returns Observable of notification actions to be dispatched
 *
 * @example
 * ```typescript
 * // In an NgRx effect:
 * postQuestionsFailure$ = createEffect(
 *   () => this.actions$.pipe(
 *     ofType(postQuestionsFailure),
 *     mergeMap(({ payload }) =>
 *       handleHttpErrorWithSnackbar(
 *         payload.error,
 *         'YourModule.ErrorMessage',
 *         this.locale
 *       )
 *     ),
 *   ),
 * );
 * ```
 */
export function handleHttpErrorWithSnackbar(
  error: HttpErrorResponse,
  fallbackMessageKey: string,
  localeService: LocaleService,
  validationKeyPrefix: string,
) {
  const errorResponse = error?.error as ApiErrorResponse | undefined;

  // Handle validation errors (400 with validationMessageCodes)
  if (errorResponse?.validationMessageCodes && Array.isArray(errorResponse.validationMessageCodes)) {
    return from(
      errorResponse.validationMessageCodes.map((code: string) =>
        NotificationActions.errorSnackbar({
          message: localeService.translate(`${validationKeyPrefix}.${code}`),
        }),
      ),
    );
  }

  // Handle generic error with custom message from backend
  if (errorResponse?.message) {
    return of(
      NotificationActions.errorSnackbar({
        message: errorResponse.message,
      }),
    );
  }

  // Handle generic error fallback (403, 500, network errors, etc.)
  return of(
    NotificationActions.errorSnackbar({
      message: localeService.translate(fallbackMessageKey),
    }),
  );
}

export function dispatchValidationErrorSnackbars(
  error: unknown,
  appStore: Store,
  localeService: LocaleService,
): void {
  const errorResponse = (error as { error?: ApiErrorResponse })?.error;

  if (!errorResponse?.validationMessageCodes?.length) {
    return;
  }

  errorResponse.validationMessageCodes.forEach((code: string) => {
    let message = localeService.translate(code);

    if (message === code) {
      const camelCaseCode = code.length ? `${code.charAt(0).toLowerCase()}${code.slice(1)}` : code;
      const groupedKeys = [
        `BFP.Validation.Backend.${code}`,
        `errors.blight.${code}`,
        `errors.downyMildew.${code}`,
        `errors.virusMonitor.${code}`,
        `errors.sprayApplications.${code}`,
        `errors.blight.${camelCaseCode}`,
        `errors.downyMildew.${camelCaseCode}`,
        `errors.virusMonitor.${camelCaseCode}`,
        `errors.sprayApplications.${camelCaseCode}`,
      ];

      for (const translationKey of groupedKeys) {
        const translation = localeService.translate(translationKey);
        if (translation !== translationKey) {
          message = translation;
          break;
        }
      }
    }

    appStore.dispatch(
      NotificationActions.errorSnackbar({
        message,
      }),
    );
  });
}
